import { type FC, useState, useCallback, useEffect } from "react";

import { cn } from "@/shared/utils/core";

export interface Story {
  id: string;
  image: string;
  title: string;
  description: string;
}

interface StoryCarouselProps {
  stories: Story[];
  intervalMs?: number;
  className?: string;
}

const DEFAULT_INTERVAL = 5_000;

export const StoryCarousel: FC<StoryCarouselProps> = ({
  stories,
  intervalMs = DEFAULT_INTERVAL,
  className,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [animKey, setAnimKey] = useState(0);

  const advance = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % stories.length);
    setAnimKey((k) => k + 1);
  }, [stories.length]);

  useEffect(() => {
    const timer = setTimeout(advance, intervalMs + 500);
    return () => clearTimeout(timer);
  }, [activeIndex, animKey, intervalMs, advance]);

  const handleIndicatorClick = (index: number) => {
    if (index !== activeIndex) {
      setActiveIndex(index);
      setAnimKey((k) => k + 1);
    }
  };

  const activeStory = stories[activeIndex];

  return (
    <div className={cn("relative overflow-hidden rounded-xl bg-stone-900", className)}>
      {stories.map((story, index) => (
        <img
          key={story.id}
          src={story.image}
          alt={index === activeIndex ? activeStory.title : ""}
          className="absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ease-in-out"
          style={{ opacity: index === activeIndex ? 1 : 0 }}
        />
      ))}

      <div
        className="absolute inset-0"
        style={{
          background: "linear-gradient(to top, rgba(0,0,0,0.7) 29%, rgba(0,0,0,0) 55%)",
        }}
      />

      <button
        type="button"
        onClick={advance}
        aria-label="Next story"
        className="absolute inset-0 cursor-pointer"
      />

      <div className="pointer-events-none relative flex h-full flex-col justify-end p-8 pb-6">
        <h3 className="text-2xl font-bold leading-tight text-white">{activeStory.title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-white/90">{activeStory.description}</p>

        <div className="pointer-events-auto mt-6 flex justify-center gap-2">
          {stories.map((story, index) => (
            <button
              key={story.id}
              type="button"
              onClick={() => handleIndicatorClick(index)}
              className="h-1.5 w-6 cursor-pointer overflow-hidden rounded-lg bg-stone-500"
              aria-label={`Story ${index + 1}: ${story.title}`}
            >
              {index < activeIndex ? (
                <div className="h-full w-full rounded-lg bg-white" />
              ) : index === activeIndex ? (
                <div
                  key={animKey}
                  className="h-full w-full origin-left rounded-lg bg-white"
                  style={{
                    animation: `story-fill ${intervalMs}ms linear forwards`,
                  }}
                  onAnimationEnd={advance}
                />
              ) : null}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
